import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import ParallaxScene from 'madow-tech/components/parallax-scene';
import LazyMarsCanvas from 'madow-tech/components/lazy-mars-canvas';
import DebugPanel from 'madow-tech/components/debug-panel';
import parallaxLayer from 'madow-tech/modifiers/parallax-layer';
import RocketButton from 'madow-tech/components/rocket-button';
import revealOnScroll from 'madow-tech/modifiers/reveal-on-scroll';
import fillViewport from 'madow-tech/modifiers/fill-viewport';
import type DebugService from 'madow-tech/services/debug';
import type MarsCanvasRegistryService from 'madow-tech/services/mars-canvas-registry';

const NAVBAR_HEIGHT = 78;

export default class HeroSectionV2 extends Component {
  @service deviceDetection;
  @service declare debug: DebugService;
  @service declare marsCanvasRegistry: MarsCanvasRegistryService;

  @tracked clickTimes: number[] = [];

  @action
  handleLogoClick(event: MouseEvent | TouchEvent) {
    // Prevent default to avoid any unwanted behavior
    // Note: For touch events, we handle preventDefault in the action itself
    if (event.type === 'touchend') {
      event.preventDefault();
    }
    event.stopPropagation();

    const now = performance.now();
    this.clickTimes.push(now);

    // Keep only last 3 clicks
    if (this.clickTimes.length > 3) {
      this.clickTimes.shift();
    }

    // Check if 3 clicks within 500ms
    if (this.clickTimes.length === 3) {
      const timeSpan = this.clickTimes[2] - this.clickTimes[0];
      if (timeSpan < 500) {
        this.debug.toggle();
        this.clickTimes = []; // Reset
      } else {
        // If too slow, keep only the last click
        this.clickTimes = [this.clickTimes[2]];
      }
    }

    // Clear old clicks after 1 second
    setTimeout(() => {
      const currentTime = performance.now();
      this.clickTimes = this.clickTimes.filter(time => currentTime - time < 1000);
    }, 1000);
  }

  <template>
    <section
      class="relative min-h-screen landscape:min-h-[886px] md:min-h-[886px]"
      {{fillViewport offset=NAVBAR_HEIGHT}}
    >
      <ParallaxScene @height="h-full" class="!h-full">
        <LazyMarsCanvas />
        <div
          class="relative z-10 landscape:pt-0 pt-20 sm:pt-20 pb-16 sm:pb-10 flex flex-col items-center h-full"
        >
          <div
            class="relative z-10 flex flex-col items-center justify-center gap-6 sm:gap-8 h-full"
          >
            <img
              src="/madow-1.svg"
              alt="Madow Tech logo - Software Factory"
              width="375"
              height="375"
              fetchpriority="high"
              class="relative inset-0 min-w-52 w-[75%] min-[400px]:w-[75%] max-w-[375px] object-contain opacity-100 z-30 chromatic-logo cursor-pointer"
              {{parallaxLayer 0.3 axis="y"}}
              {{revealOnScroll
                animation="fade-up"
                staggerChildren=true
                stagger=50
              }}
              {{on "click" this.handleLogoClick}}
              {{on "touchend" this.handleLogoClick}}
              role="button"
              tabindex="0"
              aria-label="Madow Tech logo - Triple click to toggle debug mode"
            />
            <div
              class="relative mx-auto max-w-3xl px-4 flex flex-col z-40 top-0 sm:top-[4%]"
              {{parallaxLayer 0.25 axis="y"}}
            >
              <h1
                class="p-4 text-[1.4rem] min-[400px]:text-[1.6rem] sm:text-4xl md:text-5xl font-extrabold leading-tight text-center"
                {{revealOnScroll
                  animation="fade-up"
                  staggerChildren=true
                  stagger=60
                }}
              >{{t "hero.headline"}}</h1>
              <p
                class="text-xs sm:text-sm text-center text-zinc-400 mt-8 mb-2"
                {{revealOnScroll
                  animation="fade-up"
                  staggerChildren=true
                  stagger=65
                }}
              >
                {{t "hero.subline"}}
              </p>
              <p
                class="pt-0 p-4 pb-6 mt-4 hidden sm:block text-zinc-300 md:text-lg text-center"
                {{revealOnScroll
                  animation="fade-up"
                  staggerChildren=true
                  stagger=70
                }}
              >
                {{t "hero.subhead"}}
              </p>
              <p
                class="pt-0 p-4 pb-6 text-sm sm:hidden text-zinc-300 text-center"
                {{revealOnScroll
                  animation="fade-up"
                  staggerChildren=true
                  stagger=80
                }}
              >
                {{t "hero.mobileSubhead"}}
              </p>
              <RocketButton
                @text={{t "hero.cta.primary"}}
                class="!p-3 sm:!hidden mt-8"
                {{revealOnScroll
                  animation="fade-up"
                  staggerChildren=true
                  stagger=90
                }}
              />
            </div>
          </div>
        </div>
      </ParallaxScene>

      {{#if this.debug.isEnabled}}
        {{#if this.marsCanvasRegistry.marsCanvas}}
          <DebugPanel @marsCanvas={{this.marsCanvasRegistry.marsCanvas}} />
        {{/if}}
      {{/if}}
    </section>
  </template>
}
